import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { FormikField } from '../../FormikField';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button, Button as PrimeButton } from 'primereact/button';
import { Divider } from 'primereact/divider';
import styles from './TableFilters.styles.scss';
import cn from 'classnames';

const TableFilters = forwardRef(
  (
    {
      onSubmitFilters,
      currentFilters,
      onResetFormClick,
      convertDataToForm,
      filters = [],
      filtersConfig = [],
      children,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    const formRef = useRef();
    const filterDialogRef = useRef();
    const initialValues = convertDataToForm(currentFilters);
    const [isMoreFiltersExpanded, setIsMoreFiltersExpanded] = useState(false);

    useImperativeHandle(ref, () => ({
      formRef,
    }));

    const renderFilterSections = (config) => {
      return config.map((filter, idx) => (
        <React.Fragment key={filter.title}>
          <div className="col-12 d-flex flex-column">
            <p className={styles.smallLabel}>{t(filter.title)}</p>
            <div className={cn(styles.filterRow, 'd-flex gap-3 flex-wrap')}>
              {filter.fields.map((field) => (
                <FormikField
                  name={field.name}
                  key={field.name}
                  component={field.component}
                  controlProps={field.controlProps}
                  showError={true}
                  componentProps={{
                    ...field.componentProps,
                    className: filter.fields.length === 1 ? 'w-100' : '',
                  }}
                />
              ))}
            </div>
          </div>
          {idx < config.length - 1 && <Divider />}
        </React.Fragment>
      ));
    };

    return (
      <div className={cn('my-3', styles.container)}>
        <Formik
          innerRef={formRef}
          validateOnMount
          validateOnChange
          validateOnBlur
          enableReinitialize
          validationSchema={filtersConfig.validationSchema}
          initialValues={initialValues}
          onSubmit={(e) => {
            onSubmitFilters(e);
            filterDialogRef.current.hide();
          }}
          onReset={(v) => {}}
        >
          {({ resetForm, values, submitCount }) => {
            return (
              <Form id="filters">
                <div className="d-flex justify-content-end align-items-center gap-3">
                  {children && (
                    <div className="d-flex flex-grow-1 col-md-3">
                      {children}
                    </div>
                  )}
                  <div className="d-flex">
                    <PrimeButton
                      label={t('common.advanced_filter')}
                      icon="pi pi-sliders-v"
                      iconPos="right"
                      className={cn(styles.filterBtn, 'px-5')}
                      type="button"
                      onClick={(e) => {
                        filterDialogRef.current.toggle(e);
                      }}
                    />
                  </div>
                  <OverlayPanel
                    className={styles.filtersOverlay}
                    ref={filterDialogRef}
                  >
                    <div
                      className={cn(
                        'col-12 d-flex align-items-center justify-content-between mb-3',
                        styles.filterHeader,
                      )}
                    >
                      <p>{t('common.advanced_filter')}</p>
                      <Button
                        text
                        severity="secondary"
                        className="p-0"
                        icon="pi pi-times"
                        onClick={() => filterDialogRef.current.hide()}
                      />
                    </div>
                    {renderFilterSections(
                      filtersConfig.filters(values, filters, styles, t),
                    )}

                    {filtersConfig.moreFilters && (
                      <>
                        <Divider />
                        <div className="col-12 d-flex align-items-center gap-3 mb-3">
                          <PrimeButton
                            type="button"
                            text
                            className="d-flex justify-content-start align-items-center gap-2 p-0"
                            style={{
                              fontWeight: 'bold',
                              color: '#000',
                              fontSize: '16px',
                            }}
                            onClick={() =>
                              setIsMoreFiltersExpanded(!isMoreFiltersExpanded)
                            }
                          >
                            <span>
                              {filtersConfig.moreFiltersLabel
                                ? filtersConfig.moreFiltersLabel(t)
                                : t('invoice_list.more_filters')}
                            </span>
                          </PrimeButton>
                          <Divider style={{ flex: 1, margin: 0 }} />
                          <i
                            className={`pi ${
                              isMoreFiltersExpanded
                                ? 'pi-chevron-up'
                                : 'pi-chevron-down'
                            }`}
                            style={{
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              color: '#000',
                            }}
                            onClick={() =>
                              setIsMoreFiltersExpanded(!isMoreFiltersExpanded)
                            }
                          />
                        </div>
                        {isMoreFiltersExpanded && (
                          <>
                            {renderFilterSections(
                              filtersConfig.moreFilters(
                                values,
                                filters,
                                styles,
                                t,
                              ),
                            )}
                          </>
                        )}
                      </>
                    )}

                    <div className={cn(styles.buttons, 'mt-4')}>
                      <PrimeButton
                        outlined
                        onClick={() => {
                          onResetFormClick(resetForm);
                        }}
                        className={cn('col-4', styles.resetButton)}
                        variant="outlined"
                        id="manage_reset_btn"
                        label={t('common.clear')}
                      />
                      <PrimeButton
                        disabled={
                          values &&
                          Object.values(values)?.filter((v) => !!v).length === 0
                        }
                        type="submit"
                        className="col-4"
                        id="manage_filter_btn"
                        form="filters"
                        label={t('common.apply')}
                      />
                    </div>
                  </OverlayPanel>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  },
);

export default React.memo(TableFilters);
